import { type DragEvent, type FormEvent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link, useParams } from 'react-router-dom';
import { useAuth } from '../auth/AuthContext';
import { useIsMobile } from '../hooks/useIsMobile';
import { workspacesApi, type WorkspaceMemberInfo } from '../workspaces/workspacesApi';
import { boardColumnsApi, type BoardColumn } from '../boardColumns/boardColumnsApi';
import { labelsApi, type Label } from '../labels/labelsApi';
import { sprintsApi, type Sprint } from '../sprints/sprintsApi';
import { tasksApi, type Task, type UpdateTaskRequest } from '../tasks/tasksApi';
import RightPanel from '../components/RightPanel';
import TaskPanel, { type TaskPanelHandle } from '../components/TaskPanel';
import TaskListRow from '../tasks/TaskListRow';
import ImportTasksPanel from '../components/ImportTasksPanel';
import { DeleteButton } from '../components/DeleteButton';
import { GripIcon } from '../components/icons';


const ALL_FILTER = 'ALL';
const PANEL_W = 440;

export default function SprintBoardPage() {
  const { workspaceId = '', sprintId = '' } = useParams<{ workspaceId: string; sprintId: string }>();
  const { user, getToken, refresh } = useAuth();
  const isMobile = useIsMobile();

  const [sprint, setSprint] = useState<Sprint | null>(null);
  const [tasks, setTasks] = useState<Task[]>([]);
  const [columns, setColumns] = useState<BoardColumn[]>([]);
  const [labels, setLabels] = useState<Label[]>([]);
  const [members, setMembers] = useState<WorkspaceMemberInfo[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const [assigneeFilter, setAssigneeFilter] = useState(ALL_FILTER);
  const [pointsFilter, setPointsFilter] = useState(ALL_FILTER);
  const [labelFilter, setLabelFilter] = useState(ALL_FILTER);
  const [dragOverColumnId, setDragOverColumnId] = useState<string | null>(null);

  const [viewMode, setViewMode] = useState<'board' | 'list'>(() => isMobile ? 'list' : 'board');

  // panelMode: null = closed, 'create' = create panel, taskId = task detail
  const [panelMode, setPanelMode] = useState<null | 'create' | string>(null);
  const createPanelRef = useRef<TaskPanelHandle>(null);
  const viewPanelRef = useRef<TaskPanelHandle>(null);

  // Import tasks panel
  const [importPanelOpen, setImportPanelOpen] = useState(false);

  // Task clicked from either the sprint board OR the import panel.
  // Sprint tasks are found via tasks.find; import-panel tasks (not in sprint) are stored here.
  const [viewingTask, setViewingTask] = useState<Task | null>(null);

  const taskPanelOpen = panelMode !== null;
  const openPanelCount = (taskPanelOpen ? 1 : 0) + (importPanelOpen ? 1 : 0);
  const mainMarginRight = !isMobile && openPanelCount > 0 ? openPanelCount * PANEL_W + 16 : 0;

  // Sprint task takes priority (most up-to-date); fall back to import-panel task
  const selectedTask = useMemo(() => {
    if (!panelMode || panelMode === 'create') return null;
    return tasks.find(t => t.taskId === panelMode) ?? (viewingTask?.taskId === panelMode ? viewingTask : null);
  }, [tasks, panelMode, viewingTask]);

  const sprintTaskIds = useMemo(() => new Set(tasks.map(t => t.taskId)), [tasks]);

  const [managingColumns, setManagingColumns] = useState(false);
  const [newColName, setNewColName] = useState('');
  const [addingCol, setAddingCol] = useState(false);
  const [editingColNames, setEditingColNames] = useState<Record<string, string>>({});
  const [dragOverColMgmt, setDragOverColMgmt] = useState<string | null>(null);
  const dragColRef = useRef<string | null>(null);

  const loadColumns = useCallback(() => {
    boardColumnsApi.list(workspaceId, getToken, refresh)
      .then(data => setColumns(data.columns))
      .catch(() => {});
  }, [workspaceId, getToken, refresh]);

  const loadSprint = useCallback(() => {
    sprintsApi.get(workspaceId, sprintId, getToken, refresh)
      .then(setSprint)
      .catch(() => {});
  }, [workspaceId, sprintId, getToken, refresh]);

  const loadTasks = useCallback(() => {
    setLoading(true);
    tasksApi.listForSprint(workspaceId, sprintId, getToken, refresh)
      .then(data => setTasks(data.tasks))
      .catch(() => setError('Failed to load tasks'))
      .finally(() => setLoading(false));
  }, [workspaceId, sprintId, getToken, refresh]);

  const loadLabels = useCallback(() => {
    labelsApi.list(workspaceId, getToken, refresh)
      .then(data => setLabels(data.labels))
      .catch(() => {});
  }, [workspaceId, getToken, refresh]);

  const loadMembers = useCallback(() => {
    workspacesApi.listMembers(workspaceId, getToken, refresh)
      .then(data => setMembers(data.members))
      .catch(() => {});
  }, [workspaceId, getToken, refresh]);

  useEffect(() => { loadSprint(); }, [loadSprint]);
  useEffect(() => { loadTasks(); }, [loadTasks]);
  useEffect(() => { loadColumns(); }, [loadColumns]);
  useEffect(() => { loadLabels(); }, [loadLabels]);
  useEffect(() => { loadMembers(); }, [loadMembers]);

  // Close task panel only when a SPRINT task it was showing has been deleted.
  // Don't close when showing a non-sprint task clicked from the import panel.
  useEffect(() => {
    if (!panelMode || panelMode === 'create') return;
    const isSprintTask = tasks.some(t => t.taskId === panelMode);
    const isImportTask = viewingTask?.taskId === panelMode;
    if (!isSprintTask && !isImportTask) {
      setPanelMode(null);
      setViewingTask(null);
    }
  }, [tasks, panelMode, viewingTask]);

  async function handleTaskUpdate(taskId: string, updates: UpdateTaskRequest) {
    const updated = await tasksApi.update(workspaceId, taskId, updates, getToken, refresh);
    setTasks(prev => prev.map(t => (t.taskId === taskId ? updated : t)));
    // Keep viewingTask in sync when editing a non-sprint task from the import panel
    setViewingTask(prev => (prev?.taskId === taskId ? updated : prev));
    return updated;
  }

  async function handleTaskDelete(task: Task) {
    try {
      await tasksApi.remove(workspaceId, task.taskId, getToken, refresh);
      setTasks(prev => prev.filter(t => t.taskId !== task.taskId));
      if (panelMode === task.taskId) { setPanelMode(null); setViewingTask(null); }
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete task');
    }
  }

  function handleTaskCreated(task: Task) {
    setTasks(prev => [task, ...prev]);
    setPanelMode(task.taskId);
    setViewingTask(task);
  }

  async function handleClosePanel() {
    if (panelMode === 'create') {
      await createPanelRef.current?.trySubmit();
    }
    setPanelMode(null);
    setViewingTask(null);
  }

  async function handleImport(taskIds: string[]) {
    for (const taskId of taskIds) {
      await tasksApi.update(workspaceId, taskId, { sprintId }, getToken, refresh);
    }
    loadTasks();
    setImportPanelOpen(false);
  }

  function handleDragStart(e: DragEvent, taskId: string) {
    e.dataTransfer.setData('text/plain', taskId);
    e.dataTransfer.effectAllowed = 'move';
  }

  function handleDragOver(e: DragEvent, columnId: string) {
    if (!e.dataTransfer.types.includes('text/plain')) return;
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
    if (dragOverColumnId !== columnId) setDragOverColumnId(columnId);
  }

  async function handleDrop(e: DragEvent, columnId: string) {
    if (!e.dataTransfer.types.includes('text/plain')) return;
    e.preventDefault();
    setDragOverColumnId(null);
    const taskId = e.dataTransfer.getData('text/plain');
    const task = tasks.find(t => t.taskId === taskId);
    if (!task || task.status === columnId) return;
    try {
      await handleTaskUpdate(taskId, { status: columnId });
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to update task');
    }
  }

  async function handleAddColumn(e: FormEvent) {
    e.preventDefault();
    if (!newColName.trim()) return;
    setAddingCol(true);
    try {
      await boardColumnsApi.create(workspaceId, newColName.trim(), getToken, refresh);
      setNewColName('');
      loadColumns();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to create column');
    } finally {
      setAddingCol(false);
    }
  }

  async function handleRenameColumn(columnId: string, name: string) {
    try {
      await boardColumnsApi.update(workspaceId, columnId, name, getToken, refresh);
      setEditingColNames(prev => { const next = { ...prev }; delete next[columnId]; return next; });
      loadColumns();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to rename column');
    }
  }

  async function handleDeleteColumn(columnId: string) {
    try {
      await boardColumnsApi.remove(workspaceId, columnId, getToken, refresh);
      loadColumns();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to delete column');
    }
  }

  function handleColDragStart(e: DragEvent, columnId: string) {
    dragColRef.current = columnId;
    e.dataTransfer.setData('application/x-column', columnId);
    e.dataTransfer.effectAllowed = 'move';
  }

  function handleColDragOver(e: DragEvent, columnId: string) {
    if (!e.dataTransfer.types.includes('application/x-column')) return;
    e.preventDefault();
    setDragOverColMgmt(columnId);
  }

  async function handleColDrop(e: DragEvent, targetColumnId: string) {
    if (!e.dataTransfer.types.includes('application/x-column')) return;
    e.preventDefault();
    setDragOverColMgmt(null);
    const sourceId = dragColRef.current ?? e.dataTransfer.getData('application/x-column');
    dragColRef.current = null;
    if (!sourceId || sourceId === targetColumnId) return;
    const ids = columns.map(c => c.columnId);
    const fromIdx = ids.indexOf(sourceId);
    const toIdx = ids.indexOf(targetColumnId);
    if (fromIdx === -1 || toIdx === -1) return;
    const reordered = [...ids];
    reordered.splice(fromIdx, 1);
    reordered.splice(toIdx, 0, sourceId);
    try {
      await boardColumnsApi.reorder(workspaceId, reordered, getToken, refresh);
      loadColumns();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to reorder columns');
    }
  }

  const pointOptions = useMemo(
    () => Array.from(new Set(tasks.map(task => task.points))).sort((a, b) => a - b),
    [tasks],
  );

  const filteredTasks = useMemo(
    () => tasks.filter(task => {
      if (assigneeFilter !== ALL_FILTER && !task.assigneeIds.includes(assigneeFilter)) return false;
      if (pointsFilter !== ALL_FILTER && task.points !== Number(pointsFilter)) return false;
      if (labelFilter !== ALL_FILTER && !task.labelIds.includes(labelFilter)) return false;
      return true;
    }),
    [tasks, assigneeFilter, pointsFilter, labelFilter],
  );

  const columnIds = new Set(columns.map(c => c.columnId));
  const orphanTasks = filteredTasks.filter(t => !columnIds.has(t.status));

  return (
    <main
      className="page page--wide"
      style={{
        marginRight: mainMarginRight || undefined,
        transition: 'margin-right 0.32s cubic-bezier(0.16, 1, 0.3, 1)',
      }}
    >
      <p><Link to={`/workspaces/${workspaceId}/sprints`}>← Sprints</Link></p>

      <div className="toolbar--split" style={{ marginBottom: 8 }}>
        <h1 style={{ margin: 0 }}>{sprint ? sprint.name : 'Sprint Board'}</h1>
        <div className="toolbar">
          <div style={{ display: 'flex', border: '1px solid var(--color-border)', borderRadius: 6, overflow: 'hidden' }}>
            <button
              type="button"
              onClick={() => { setViewMode('board'); if (panelMode !== 'create') setPanelMode(null); }}
              title="Board view"
              style={{ padding: '4px 10px', fontSize: 13, background: viewMode === 'board' ? 'var(--color-primary)' : 'transparent', color: viewMode === 'board' ? '#fff' : 'var(--color-text-muted)', border: 'none', cursor: 'pointer' }}
            >⊞ Board</button>
            <button
              type="button"
              onClick={() => setViewMode('list')}
              title="List view"
              style={{ padding: '4px 10px', fontSize: 13, background: viewMode === 'list' ? 'var(--color-primary)' : 'transparent', color: viewMode === 'list' ? '#fff' : 'var(--color-text-muted)', border: 'none', cursor: 'pointer' }}
            >☰ List</button>
          </div>
          <button type="button" onClick={() => setManagingColumns(v => !v)}>
            {managingColumns ? 'Done' : 'Manage columns'}
          </button>
          <button
            type="button"
            onClick={() => setImportPanelOpen(v => !v)}
            style={importPanelOpen ? { background: 'var(--color-primary)', color: '#fff' } : undefined}
          >
            ↓ Import Tasks
          </button>
          <button
            type="button"
            onClick={() => setPanelMode(prev => (prev === 'create' ? null : 'create'))}
            style={panelMode === 'create' ? { background: 'var(--color-primary)', color: '#fff' } : undefined}
          >
            + Create Task
          </button>
        </div>
      </div>

      {error && <p className="error">{error}</p>}
      {loading && <p className="muted">Loading…</p>}

      {managingColumns && (
        <div className="form-card" style={{ marginBottom: 16 }}>
          <strong style={{ fontSize: 13 }}>Columns</strong>
          <ul style={{ listStyle: 'none', padding: 0, margin: '8px 0', display: 'flex', flexDirection: 'column', gap: 6 }}>
            {columns.map(col => (
              <li
                key={col.columnId}
                draggable
                onDragStart={e => handleColDragStart(e, col.columnId)}
                onDragOver={e => handleColDragOver(e, col.columnId)}
                onDragLeave={() => setDragOverColMgmt(prev => (prev === col.columnId ? null : prev))}
                onDrop={e => handleColDrop(e, col.columnId)}
                style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '4px 6px', background: dragOverColMgmt === col.columnId ? 'var(--color-border)' : undefined, borderRadius: 4, cursor: 'grab' }}
              >
                <GripIcon size={14} />
                <input
                  value={editingColNames[col.columnId] ?? col.name}
                  onChange={e => setEditingColNames(prev => ({ ...prev, [col.columnId]: e.target.value }))}
                  onBlur={() => {
                    const newName = editingColNames[col.columnId];
                    if (newName !== undefined && newName.trim() && newName.trim() !== col.name) {
                      handleRenameColumn(col.columnId, newName.trim());
                    } else {
                      setEditingColNames(prev => { const next = { ...prev }; delete next[col.columnId]; return next; });
                    }
                  }}
                  style={{ flex: 1, fontSize: 13 }}
                  onClick={e => e.stopPropagation()}
                />
                <DeleteButton onConfirm={() => handleDeleteColumn(col.columnId)} title="Delete column" />
              </li>
            ))}
          </ul>
          <form onSubmit={handleAddColumn} style={{ display: 'flex', gap: 8 }}>
            <input value={newColName} onChange={e => setNewColName(e.target.value)} placeholder="New column name" />
            <button type="submit" disabled={addingCol || !newColName.trim()}>{addingCol ? 'Adding…' : 'Add column'}</button>
          </form>
        </div>
      )}

      <div style={{ display: 'flex', gap: 16, alignItems: 'center', flexWrap: 'wrap', margin: '16px 0' }}>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
          Assignee
          <select value={assigneeFilter} onChange={e => setAssigneeFilter(e.target.value)}>
            <option value={ALL_FILTER}>All</option>
            {members.map(m => <option key={m.userId} value={m.userId}>{m.displayName}</option>)}
          </select>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
          Points
          <select value={pointsFilter} onChange={e => setPointsFilter(e.target.value)}>
            <option value={ALL_FILTER}>All</option>
            {pointOptions.map(pts => <option key={pts} value={pts}>{pts}</option>)}
          </select>
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: 4, fontSize: 12 }}>
          Label
          <select value={labelFilter} onChange={e => setLabelFilter(e.target.value)}>
            <option value={ALL_FILTER}>All</option>
            {labels.map(l => <option key={l.labelId} value={l.labelId}>{l.name}</option>)}
          </select>
        </label>
      </div>

      {/* List view */}
      {viewMode === 'list' && (
        <div style={{ border: '1px solid var(--color-border)', borderRadius: 8, overflow: 'hidden' }}>
          {filteredTasks.length === 0 && (
            <p className="muted" style={{ padding: '12px 16px', margin: 0 }}>No tasks.</p>
          )}
          {filteredTasks.map(task => (
            <TaskListRow
              key={task.taskId}
              task={task}
              columns={columns}
              labels={labels}
              members={members}
              selected={panelMode === task.taskId}
              onSelect={() => setPanelMode(prev => prev === task.taskId ? null : task.taskId)}
              onStatusChange={status => handleTaskUpdate(task.taskId, { status }).catch(() => setError('Failed to update task'))}
            />
          ))}
        </div>
      )}

      {/* Board view */}
      <div className="board-columns" style={{ display: viewMode === 'board' ? undefined : 'none' }}>
        {columns.map(col => (
          <div
            key={col.columnId}
            className={`board-column${dragOverColumnId === col.columnId ? ' board-column--over' : ''}`}
            onDragOver={e => handleDragOver(e, col.columnId)}
            onDragLeave={() => setDragOverColumnId(prev => (prev === col.columnId ? null : prev))}
            onDrop={e => handleDrop(e, col.columnId)}
          >
            <h2 style={{ marginTop: 0, fontSize: 14 }}>{col.name}</h2>
            <div className="board-column-cards">
              {filteredTasks.filter(task => task.status === col.columnId).map(task => {
                const taskLabels = labels.filter(l => task.labelIds.includes(l.labelId));
                const assignees = members.filter(m => task.assigneeIds.includes(m.userId));
                const borderColor = taskLabels[0]?.color ?? '#ccc';
                const isSelected = panelMode === task.taskId;
                return (
                  <div
                    key={task.taskId}
                    draggable
                    onDragStart={e => handleDragStart(e, task.taskId)}
                    onClick={() => setPanelMode(prev => (prev === task.taskId ? null : task.taskId))}
                    className="task-card"
                    style={{ borderLeft: `4px solid ${borderColor}`, cursor: 'pointer', background: isSelected ? 'rgba(59,130,246,0.06)' : undefined, outline: isSelected ? '2px solid var(--color-primary)' : undefined }}
                  >
                    <strong style={{ fontSize: 13, lineHeight: 1.4, display: 'block' }}>{task.title}</strong>
                    {taskLabels.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, marginTop: 4 }}>
                        {taskLabels.map(l => (
                          <span key={l.labelId} style={{ fontSize: 10, background: l.color, color: '#fff', borderRadius: 10, padding: '1px 6px' }}>{l.name}</span>
                        ))}
                      </div>
                    )}
                    {assignees.length > 0 && (
                      <p className="muted" style={{ margin: '3px 0 0', fontSize: 11 }}>{assignees.map(m => m.displayName.split(' ')[0]).join(', ')}</p>
                    )}
                    {task.points > 0 && (
                      <span className="badge" style={{ marginTop: 4, display: 'inline-block' }}>{task.points} pt</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        ))}
        {orphanTasks.length > 0 && (
          <div className="board-column">
            <h2 style={{ marginTop: 0, fontSize: 14, color: 'var(--color-text-muted)' }}>Other</h2>
            <div className="board-column-cards">
              {orphanTasks.map(task => {
                const taskLabels = labels.filter(l => task.labelIds.includes(l.labelId));
                const assignees = members.filter(m => task.assigneeIds.includes(m.userId));
                const borderColor = taskLabels[0]?.color ?? '#ccc';
                const isSelected = panelMode === task.taskId;
                return (
                  <div
                    key={task.taskId}
                    draggable
                    onDragStart={e => handleDragStart(e, task.taskId)}
                    onClick={() => setPanelMode(prev => (prev === task.taskId ? null : task.taskId))}
                    className="task-card"
                    style={{ borderLeft: `4px solid ${borderColor}`, cursor: 'pointer', background: isSelected ? 'rgba(59,130,246,0.06)' : undefined, outline: isSelected ? '2px solid var(--color-primary)' : undefined }}
                  >
                    <strong style={{ fontSize: 13, lineHeight: 1.4, display: 'block' }}>{task.title}</strong>
                    {taskLabels.length > 0 && (
                      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 3, marginTop: 4 }}>
                        {taskLabels.map(l => (
                          <span key={l.labelId} style={{ fontSize: 10, background: l.color, color: '#fff', borderRadius: 10, padding: '1px 6px' }}>{l.name}</span>
                        ))}
                      </div>
                    )}
                    {assignees.length > 0 && (
                      <p className="muted" style={{ margin: '3px 0 0', fontSize: 11 }}>{assignees.map(m => m.displayName.split(' ')[0]).join(', ')}</p>
                    )}
                    {task.points > 0 && (
                      <span className="badge" style={{ marginTop: 4, display: 'inline-block' }}>{task.points} pt</span>
                    )}
                  </div>
                );
              })}
            </div>
          </div>
        )}
      </div>

      {/* ── Panels ─────────────────────────────────────────────────────────── */}

      {/* Import tasks panel — always rightmost */}
      {importPanelOpen && (
        <RightPanel
          title="Import Tasks"
          rightOffset={0}
          zIndex={52}
          onClose={() => setImportPanelOpen(false)}
        >
          <ImportTasksPanel
            workspaceId={workspaceId}
            sprintId={sprintId}
            sprintTaskIds={sprintTaskIds}
            columns={columns}
            labels={labels}
            members={members}
            getToken={getToken}
            doRefresh={refresh}
            onTaskClick={task => {
              setViewingTask(task);
              setPanelMode(prev => (prev === task.taskId ? null : task.taskId));
            }}
            onImport={handleImport}
            onCancel={() => setImportPanelOpen(false)}
          />
        </RightPanel>
      )}

      {/* Task create / detail panel — sits to the left of import panel when both open */}
      {panelMode === 'create' && user && (
        <RightPanel
          title="Create Task"
          rightOffset={importPanelOpen ? PANEL_W : 0}
          zIndex={51}
          onClose={handleClosePanel}
        >
          <TaskPanel
            ref={createPanelRef}
            mode="create"
            workspaceId={workspaceId}
            defaultSprintId={sprintId}
            currentUserId={user.userId}
            columns={columns}
            labels={labels}
            members={members}
            getToken={getToken}
            doRefresh={refresh}
            onCreate={handleTaskCreated}
            onCancel={() => setPanelMode(null)}
          />
        </RightPanel>
      )}
      {panelMode && panelMode !== 'create' && selectedTask && (
        <RightPanel
          title="Task"
          rightOffset={importPanelOpen ? PANEL_W : 0}
          zIndex={51}
          onClose={() => { setPanelMode(null); setViewingTask(null); }}
          onEdit={() => viewPanelRef.current?.startEdit()}
          onDelete={() => handleTaskDelete(selectedTask)}
        >
          <TaskPanel
            ref={viewPanelRef}
            mode="view"
            task={selectedTask}
            workspaceId={workspaceId}
            columns={columns}
            labels={labels}
            members={members}
            getToken={getToken}
            doRefresh={refresh}
            onUpdate={handleTaskUpdate}
          />
        </RightPanel>
      )}
    </main>
  );
}
